import React, { useState } from 'react';

import { AppBarErrorBoundary } from './AppBarErrorBoundary';
import {
    LazyTileContextMenuBehavior,
    LazyTileContextMenuProps,
} from './LazyTileContextMenu.types';
import { TileContextMenuCallbacks } from './TileContextMenu.types';

/**
 * Context menu for launch tiles to support pinning and other commands
 * @param onPinStateChange Event fired when pin state for a tile changes
 * @param isPinned True if the connected tile is pinned. False otherwise.
 * @returns JSX.Element
 */
export const LazyTileContextMenuRender: React.ForwardRefRenderFunction<
    LazyTileContextMenuBehavior,
    LazyTileContextMenuProps
> = (
    {
        tileContextMenu: tileContextMenuRef,
        style,
        customImportHandler = (p) => p,
        children,
    },
    ref,
) => {
    // This is the state used to determine whether the context menu is open or not.
    // Note that it has three states:
    // True for open
    // False for closed
    // null indicates that the context menu is closed and has never been opened, which
    // means we don't have to lazy load the actual implementation yet.
    const [contextMenuOpen, setContextMenuOpen] = useState<boolean | null>(
        null,
    );
    const [menuItemCallbacks, setMenuItemCallbacks] =
        useState<TileContextMenuCallbacks | null>(null);
    const [menuTarget, setMenuTarget] = useState<HTMLElement | undefined>(
        undefined,
    );
    const [targetAppId, setTargetAppId] = useState<string>('');

    React.useImperativeHandle(
        ref,
        () => ({
            open: (target, index, menuCallbacks, targetAppTileId) => {
                menuCallbacks.pinnedIndex = index;
                setTargetAppId(targetAppTileId);
                setMenuTarget(target);
                setMenuItemCallbacks(menuCallbacks);
                setContextMenuOpen(true);
            },
        }),
        [],
    );

    // Lazy renderer for the context menu
    const TileContextMenu = React.lazy(() =>
        customImportHandler(
            import(
                /* webpackChunkName: "appbar-control-tilecontextmenu" */ './TileContextMenu'
            ),
        ),
    );

    return (
        <>
            {children}
            {contextMenuOpen != null && menuItemCallbacks != null ? (
                <AppBarErrorBoundary boundarySource="LazyTileContextMenu">
                    <React.Suspense fallback="">
                        <TileContextMenu
                            tileContextMenu={tileContextMenuRef}
                            targetAppTileId={targetAppId}
                            style={style}
                            contextMenuOpen={contextMenuOpen}
                            setContextMenuOpen={setContextMenuOpen}
                            menuTarget={menuTarget}
                            menuItemCallbacks={menuItemCallbacks}
                        />
                    </React.Suspense>
                </AppBarErrorBoundary>
            ) : null}
        </>
    );
};

export const LazyTileContextMenu = React.forwardRef(LazyTileContextMenuRender);
